import type { ReactNode } from "react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowLeftRight, ArrowRight, MapPin, MessageCircle, Phone } from "lucide-react";
import type { TFunction } from "i18next";
import {
  type DataTableColumn,
  TablePill,
  TableStatus,
  TableChip,
  TableTag,
} from "@akuna/ui";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { AppointmentWithDetails } from "@/hooks/useAppointments";
import type { AppointmentStatus as ClinicStatus } from "@/hooks/useAppointmentStatuses";
import { getEffectiveAppointmentDriverType } from "@/lib/utils/appointment-driver";
import { getPrimaryOwnerAddress, formatOwnerAddressFull, getOwnerPhones } from "@/lib/appointments/owner-address";
import { AddressLink } from "@/components/shared/AddressLink";
import { maskPhoneInput } from "@/lib/utils/phone-mask";
import { getStatusSoftStyle } from "@/lib/utils/clinic-appointment-status";
import { hexToRgba } from "@/components/calendar/views/calendarEventUtils";

const whatsappHref = (phoneNumber: string): string => {
  const digits = phoneNumber.replace(/\D/g, "");
  const withCountryCode = digits.startsWith("55") ? digits : `55${digits}`;
  return `https://wa.me/${withCountryCode}`;
};

type Rec = Record<string, unknown>;
const str = (v: unknown): string => (typeof v === "string" ? v : "");
const petOf = (a: AppointmentWithDetails) => (a.pet ?? {}) as Rec;
const ownerOf = (a: AppointmentWithDetails) => (a.owner ?? {}) as Rec;

export const getPetName = (a: AppointmentWithDetails): string => str(petOf(a).name);
export const getPetId = (a: AppointmentWithDetails): string => str(petOf(a).id);
export const getResponsavel = (a: AppointmentWithDetails): string => str(ownerOf(a).name);
export const getOwnerId = (a: AppointmentWithDetails): string => str(ownerOf(a).id);
export const getHorario = (a: AppointmentWithDetails): string => format(new Date(a.starts_at), "HH:mm");
export const getRaca = (a: AppointmentWithDetails): string => {
  const breed = petOf(a).breed as Rec | null | undefined;
  return str(breed?.name);
};
export const getServicos = (a: AppointmentWithDetails): string[] =>
  (a.appointment_items ?? [])
    .filter((it) => {
      const type = (it.item?.type ?? "").toUpperCase();
      return type === "PRODUTO" || type === "SERVICO";
    })
    .map((it) => it.title_snapshot?.trim() || it.item?.name?.trim() || "")
    .filter(Boolean);

export interface TransporteFlags {
  pickup: boolean;
  dropoff: boolean;
}

export const getTransporteFlags = (a: AppointmentWithDetails): TransporteFlags => {
  const type = getEffectiveAppointmentDriverType({ driverType: a.taxi_dog_type, isTaxiDog: a.is_taxi_dog });
  return {
    pickup: type === "pickup" || type === "pickup_and_dropoff",
    dropoff: type === "dropoff" || type === "pickup_and_dropoff",
  };
};

const DEFAULT_TYPE_COLOR = "#6366f1";

const typeSoftStyle = (color: string | undefined) => {
  const resolved = color ?? DEFAULT_TYPE_COLOR;
  return {
    color: resolved,
    backgroundColor: hexToRgba(resolved, 0.14),
  };
};

const stop = (e: React.MouseEvent) => e.stopPropagation();

export interface BuildColumnsArgs {
  t: TFunction<"calendar">;
  statuses: ClinicStatus[];
}

export function buildFlowboardListColumns({ t, statuses }: BuildColumnsArgs): DataTableColumn<AppointmentWithDetails>[] {
  const tc = (k: string) => t(`listColumns.${k}` as never) as ReactNode;
  return [
    {
      id: "horario",
      header: tc("horario"),
      width: 96,
      minWidth: 80,
      sortAccessor: (a) => a.starts_at,
      cell: (a) => <span className="font-bold">{getHorario(a)}</span>,
    },
    {
      id: "tipo",
      header: tc("tipo"),
      width: 132,
      minWidth: 92,
      sortAccessor: (a) => a.clinic_appointment_type?.name ?? a.type,
      cell: (a) => (
        <TablePill style={typeSoftStyle(a.clinic_appointment_type?.color)}>
          {a.clinic_appointment_type?.name ?? a.type}
        </TablePill>
      ),
    },
    {
      id: "pet",
      header: tc("pet"),
      width: 140,
      minWidth: 96,
      sortAccessor: (a) => getPetName(a).toLowerCase(),
      cell: (a) =>
        getPetId(a) ? (
          <Link className="font-bold text-primary hover:underline" onClick={stop} to={`/patients/pets/${getPetId(a)}`}>
            {getPetName(a)}
          </Link>
        ) : (
          <span className="font-bold">{getPetName(a)}</span>
        ),
    },
    {
      id: "raca",
      header: tc("raca"),
      width: 150,
      minWidth: 100,
      sortAccessor: (a) => getRaca(a).toLowerCase(),
      cell: (a) => <span>{getRaca(a)}</span>,
    },
    {
      id: "responsavel",
      header: tc("responsavel"),
      width: 196,
      minWidth: 120,
      sortAccessor: (a) => getResponsavel(a).toLowerCase(),
      cell: (a) =>
        getOwnerId(a) ? (
          <Link className="text-primary hover:underline" onClick={stop} to={`/patients/owners/${getOwnerId(a)}`}>
            {getResponsavel(a)}
          </Link>
        ) : (
          <span>{getResponsavel(a)}</span>
        ),
    },
    {
      id: "status",
      header: tc("status"),
      width: 158,
      minWidth: 120,
      sortAccessor: (a) => statuses.find((s) => s.name === a.status)?.sort_order ?? 999,
      cell: (a) => {
        const meta = statuses.find((s) => s.name === a.status);
        return <TableStatus label={meta?.display_name ?? a.status} style={getStatusSoftStyle(a.status, statuses)} />;
      },
    },
    {
      id: "servicos",
      header: tc("servicos"),
      width: 244,
      minWidth: 140,
      sortAccessor: (a) => getServicos(a).length,
      cell: (a) => {
        const servicos = getServicos(a);
        const visible = servicos.slice(0, 2);
        const rest = servicos.slice(2);
        return (
          <div className="flex flex-wrap items-center gap-1">
            {visible.map((sv, i) => (
              <TableTag key={i}>{sv}</TableTag>
            ))}
            {rest.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span onClick={stop}>
                    <TableTag>+{rest.length}</TableTag>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex flex-col gap-1">
                    {rest.map((sv, i) => (
                      <span key={i}>{sv}</span>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      id: "transporte",
      header: tc("transporte"),
      width: 162,
      minWidth: 120,
      sortAccessor: (a) => Number(getTransporteFlags(a).pickup) + Number(getTransporteFlags(a).dropoff),
      cell: (a) => {
        const f = getTransporteFlags(a);
        if (!f.pickup && !f.dropoff) return <span className="text-muted-foreground">—</span>;
        if (f.pickup && f.dropoff)
          return <TableChip tone="blue" icon={ArrowLeftRight}>{t("driverTypePickupAndDropoff")}</TableChip>;
        if (f.pickup)
          return <TableChip tone="blue" icon={ArrowRight}>{t("driverTypePickup")}</TableChip>;
        return <TableChip tone="blue" icon={ArrowLeft}>{t("driverTypeDropoff")}</TableChip>;
      },
    },
    {
      id: "endereco",
      header: tc("endereco"),
      width: 260,
      minWidth: 160,
      sortAccessor: (a) => formatOwnerAddressFull(getPrimaryOwnerAddress(a)).toLowerCase(),
      cell: (a) => {
        const text = formatOwnerAddressFull(getPrimaryOwnerAddress(a));
        if (!text) return <span className="text-muted-foreground">—</span>;
        return (
          <AddressLink addressText={text} className="inline-flex items-center gap-1.5">
            <MapPin aria-hidden size={13} className="shrink-0" />
            <span className="truncate" title={text}>{text}</span>
          </AddressLink>
        );
      },
    },
    {
      id: "telefone",
      header: tc("telefone"),
      width: 170,
      minWidth: 130,
      sortAccessor: (a) => getOwnerPhones(a)[0]?.phone_number ?? "",
      cell: (a) => {
        const phones = getOwnerPhones(a);
        if (phones.length === 0) return <span className="text-muted-foreground">—</span>;
        return (
          <div className="flex flex-col gap-0.5">
            {phones.map((phone) => {
              const number = phone.phone_number ?? "";
              if (!number) return null;
              if (phone.is_whatsapp) {
                return (
                  <a
                    key={phone.id ?? number}
                    href={whatsappHref(number)}
                    target="_blank"
                    rel="noreferrer"
                    onClick={stop}
                    className="inline-flex items-center gap-1.5 text-primary hover:underline"
                  >
                    <MessageCircle aria-hidden size={13} className="shrink-0" />
                    {maskPhoneInput(number)}
                  </a>
                );
              }
              return (
                <span key={phone.id ?? number} className="inline-flex items-center gap-1.5">
                  <Phone aria-hidden size={13} className="shrink-0 text-muted-foreground" />
                  {maskPhoneInput(number)}
                </span>
              );
            })}
          </div>
        );
      },
    },
  ];
}
